import { z } from 'zod'
import { patchOf } from './patch.ts'

/**
 * Git-Quellen (§18.1, ADR 0019): GitHub repositories connected to a container through our GitHub App. The worker
 * syncs docs, manifests and optional code files into `source_documents`; they are indexed for search, chat, MCP and
 * the live copilot. Paths: see docs/api/sources.md.
 */

const id = z.uuid()
const isoDateTime = z.iso.datetime({ offset: true })

/** max. documents per source (repo files + overview) */
export const MAX_SOURCE_DOCUMENTS = 2000
/** files larger than this are skipped */
export const MAX_SOURCE_FILE_BYTES = 1024 * 1024
/** text stored per document */
export const MAX_SOURCE_TEXT_CHARS = 500_000
/** path of the generated repo overview document */
export const REPO_OVERVIEW_PATH = '__overview__.md'

// ── GitHub App ────────────────────────────────────────────────────────────────────────────────

export const GithubInstallation = z.object({
  id: z.number().int(),
  account: z.string(),
  accountType: z.string(),
})
export type GithubInstallation = z.infer<typeof GithubInstallation>

/** GET /github/status */
export const GithubStatus = z.object({
  /** GITHUB_APP_ID + GITHUB_APP_PRIVATE_KEY set */
  configured: z.boolean(),
  /** https://github.com/apps/<slug>/installations/new – null without GITHUB_APP_SLUG */
  installUrl: z.string().nullable(),
  /** webhook secret set (push → sync); without it only the 6-hourly sweep and manual syncs run */
  webhook: z.boolean(),
  installations: z.array(GithubInstallation),
})
export type GithubStatus = z.infer<typeof GithubStatus>

/** GET /github/repos – repos accessible through the user's installations */
export const GithubRepo = z.object({
  installationId: z.number().int(),
  repoId: z.number().int(),
  fullName: z.string(),
  private: z.boolean(),
  defaultBranch: z.string(),
})
export type GithubRepo = z.infer<typeof GithubRepo>

// ── Sources ───────────────────────────────────────────────────────────────────────────────────

export const SourceStatus = z.enum(['pending', 'syncing', 'ok', 'error'])
export type SourceStatus = z.infer<typeof SourceStatus>

/** code globs, e.g. "src/**\/*.ts" – simple glob syntax: *, **, ?, {a,b} */
const Glob = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((g) => !g.startsWith('/') && !g.includes('..'), 'Ungültiges Muster.')
const Branch = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9._/-]+$/, 'Ungültiger Branch-Name.')

export const SkippedFile = z.object({
  path: z.string(),
  /** secret rule name (never the secret itself), or 'too_large' | 'binary' | 'limit' */
  rule: z.string(),
})
export type SkippedFile = z.infer<typeof SkippedFile>

export const Source = z.object({
  id,
  containerId: id,
  kind: z.literal('git'),
  repo: z.string(),
  repoId: z.number().int(),
  installationId: z.number().int(),
  /** configured branch; null = the repo's default branch */
  branch: z.string().nullable(),
  include: z.array(z.string()),
  status: SourceStatus,
  /** German, for the UI */
  error: z.string().nullable(),
  lastSyncedAt: isoDateTime.nullable(),
  lastCommitSha: z.string().nullable(),
  documents: z.number().int(),
  /** files left out because of possible secrets (path + rule) */
  skippedSecrets: z.array(SkippedFile),
  /** more files matched than MAX_SOURCE_DOCUMENTS */
  truncated: z.boolean(),
  createdAt: isoDateTime,
  createdBy: z.object({ id, name: z.string() }).nullable(),
  htmlUrl: z.string(),
})
export type Source = z.infer<typeof Source>

/** POST /containers/:id/sources */
export const CreateSource = z.object({
  /** owner/name, must be accessible through one of the user's installations */
  repo: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, 'Repo im Format owner/name angeben.'),
  branch: Branch.nullable().optional(),
  include: z.array(Glob).max(30).default([]),
})
export type CreateSource = z.infer<typeof CreateSource>

/** PATCH /sources/:id */
export const UpdateSource = patchOf(
  z.object({
    branch: Branch.nullable(),
    include: z.array(Glob).max(30),
  }),
)
export type UpdateSource = z.infer<typeof UpdateSource>

export const SourceDocument = z.object({
  id,
  sourceId: id,
  path: z.string(),
  title: z.string(),
  size: z.number().int(),
  language: z.string().nullable(),
  htmlUrl: z.string().nullable(),
  updatedAt: isoDateTime,
  indexedAt: isoDateTime.nullable(),
})
export type SourceDocument = z.infer<typeof SourceDocument>

export const SourceDocumentDetail = SourceDocument.extend({
  containerId: id,
  repo: z.string(),
  /** markdown files are rendered, everything else shown monospaced */
  markdown: z.boolean(),
  text: z.string(),
  truncated: z.boolean(),
})
export type SourceDocumentDetail = z.infer<typeof SourceDocumentDetail>
